<script lang="ts">
import type { KbdProps, TabsEmits, TabsProps, TabsSlots, TabsItem as UTabsItem } from '#ui/types';

export interface TabsItem extends UTabsItem {
	kbds?: string[] | KbdProps[];
}
</script>

<script setup lang="ts" generic="T extends TabsItem">
import type { ComponentPublicInstance } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { tv } from 'tailwind-variants';
import { computed, useTemplateRef } from 'vue';
import { get, omit } from '#layers/base/public';
import { useCollapsibleTabs } from '#layers/ui/composables/use-collapsible-tabs';
import Tabs from '#ui/components/Tabs.vue';

const props = withDefaults(defineProps<TabsProps<T> & {
	collapsible?: boolean;
}>(), {
	labelKey: 'label',
	content: true,
	collapsible: false,
});
const emit = defineEmits<TabsEmits>();
const slots = defineSlots<TabsSlots<T>>();
const forwarded = useForwardPropsEmits(reactiveOmit(props, 'collapsible') as TabsProps<T>, emit);
const proxySlots = omit(slots, ['default', 'list-trailing']);

const tabsUi = tv({
	slots: {
		content: '',
		root: '',
		list: '',
		indicator: '',
	},
	variants: {
		content: {
			// Hide panels rather than unmount them, keeping trigger aria-controls references valid
			false: { content: 'hidden' },
		},
		collapsible: {
			true: { root: 'min-w-0', list: 'overflow-hidden' },
		},
		activeInOverflow: {
			true: { indicator: 'opacity-0' },
		},
	},
});

/**
 * Matches the overflow button radius to pill tabs at each supported size.
 * Inactive (ghost) mirrors the tab trigger radius; active (outline) mirrors the
 * active indicator pill, which sits 1px tighter to nest concentrically.
 */
const overflowTriggerVariant = tv({
	// link tabs (and any non-pill fallback) use the default button radius.
	base: 'max-w-full min-w-0 rounded-md',
	variants: {
		variant: { pill: '', link: '' },
		size: { xs: '', sm: '', md: '', lg: '', xl: '' },
		active: { true: '', false: '' },
	},
	compoundVariants: [
		{ variant: 'pill', size: ['md', 'lg', 'xl'], active: false, class: 'rounded-lg' },
		{ variant: 'pill', size: ['md', 'lg', 'xl'], active: true, class: 'rounded-[calc(var(--ui-radius)*2-1px)]' },
		{ variant: 'pill', size: ['xs', 'sm'], active: true, class: 'rounded-[calc(var(--ui-radius)*1.5-1px)]' },
	],
	defaultVariants: {
		variant: 'pill',
		size: 'md',
		active: false,
	},
});

const tabsRef = useTemplateRef<ComponentPublicInstance>('tabs');
const overflowTriggerRef = useTemplateRef<HTMLElement>('overflowTrigger');

const {
	itemsWithOverflowUi,
	overflowCount,
	hasOverflow,
	triggerMaxWidth,
	activeInOverflow,
	activeOverflowItem,
	overflowDropdownItems,
} = useCollapsibleTabs<T>({
	tabsRef,
	overflowTriggerRef,
	items: () => props.items,
	modelValue: () => props.modelValue,
	labelKey: () => props.labelKey as string,
	enabled: () => props.collapsible,
	onSelect: (value) => emit('update:modelValue', value),
});

const mergedUi = computed(() => {
	const ui = tabsUi({
		content: props.content,
		collapsible: props.collapsible,
		activeInOverflow: activeInOverflow.value,
	});

	return {
		...props.ui,
		content: ui.content({ class: props.ui?.content }),
		root: ui.root({ class: props.ui?.root }),
		list: ui.list({ class: props.ui?.list }),
		indicator: ui.indicator({ class: props.ui?.indicator }),
	};
});

const overflowTriggerStyle = computed(() => activeInOverflow.value && triggerMaxWidth.value !== undefined
	? { maxWidth: `${triggerMaxWidth.value}px` }
	: undefined);
const overflowTriggerIcon = computed(() => activeOverflowItem.value?.icon ?? 'lucide:ellipsis');
const overflowTriggerLabel = computed(() => activeInOverflow.value && activeOverflowItem.value
	? get(activeOverflowItem.value, props.labelKey as string) as string
	: undefined);
const overflowTriggerTrailingIcon = computed(() => activeInOverflow.value ? 'lucide:chevron-down' : undefined);
const overflowTriggerBaseClass = computed(() => overflowTriggerVariant({ variant: props.variant, size: props.size, active: activeInOverflow.value }));
</script>

<template>
	<Tabs
		ref="tabs"
		v-bind="forwarded"
		:items="itemsWithOverflowUi"
		content
		:ui="mergedUi"
	>
		<template v-for="(_, name) in proxySlots" #[name]="slotData: any">
			<!-- @vue-expect-error -->
			<slot :name v-bind="slotData" />
		</template>

		<template #default="slotData: any">
			<DTooltip :disabled="!slotData.item.kbds" :text="get(slotData.item, props.labelKey as string)" :kbds="slotData.item.kbds">
				<span>
					<slot v-bind="slotData">
						{{ get(slotData.item, props.labelKey as string) }}
					</slot>
				</span>
			</DTooltip>
		</template>

		<template v-if="collapsible" #list-trailing>
			<slot name="list-trailing" />

			<div v-if="hasOverflow" ref="overflowTrigger" class="flex shrink-0 items-center">
				<DDropdownMenu :items="overflowDropdownItems" :size="props.size">
					<DButton
						:variant="activeInOverflow ? 'outline' : 'ghost'"
						color="neutral"
						:size="props.size"
						:icon="overflowTriggerIcon"
						:trailing-icon="overflowTriggerTrailingIcon"
						:label="overflowTriggerLabel"
						:aria-label="overflowTriggerLabel ? undefined : $t('common.n_more', overflowCount)"
						:style="overflowTriggerStyle"
						:ui="{
							base: overflowTriggerBaseClass,
							label: 'truncate',
							leadingIcon: 'shrink-0',
							trailingIcon: 'shrink-0',
						}"
					/>
				</DDropdownMenu>
			</div>
		</template>

		<template v-else #list-trailing>
			<slot name="list-trailing" />
		</template>
	</Tabs>
</template>
